import { put, call, takeLatest, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import client from '../client';
import { webhookActions } from '../reducers/webhook';
import { setLoading } from '../loading/loadingActions';
import { ISecret, WebhookState, ICertificates } from '../models/webhook';
import {
  ILoadWebhookDetailsAction,
  IGenerateNewSecretAction,
  IDeleteWebhookAction,
  IPauseResumeWebhookAction,
  IUpdateWebhookAction,
  IUploadCertificate,
  IDeleteWebhookCertificate
} from '../actionTypes/webhook';

export function* loadWebhookDetails({ payload: { appId, webhookId } }: PayloadAction<ILoadWebhookDetailsAction>) {
  try {
    const response: WebhookState = yield call(client, { url: `webhooks/${appId}/${webhookId}` });
    yield put(webhookActions.loadWebhookDetailsSuccess(response));
  } catch (error: any) {
    yield put(webhookActions.loadWebhookDetailsFail(error));
  } finally {
    setLoading(false);
  }
}

export function* generateNewSecret({ payload: { res, rej } }: PayloadAction<IGenerateNewSecretAction>) {
  const state = yield select();
  const {
    apps: { appDetails: { data } },
    webhook: { webhookDetails },
  } = state;
  const appId = data['id'];
  const webhookId = webhookDetails['id'];
  try {
    const response: ISecret[] = yield call(client, {
      url: `webhooks/${appId}/${webhookId}/secrets`,
      method: 'put',
    });
    yield put(webhookActions.generateNewSecretSuccess(response));
    if (typeof res === 'function') {
      yield call(res);
    }
  } catch (error: any) {
    yield put(webhookActions.generateNewSecretFail(error));
    if (typeof rej === 'function') {
      yield call(rej);
    }
  }
}

export function* deleteWebhook({ payload: { res, rej } }: PayloadAction<IDeleteWebhookAction>) {
  setLoading(true);
  const state = yield select();
  const {
    apps: { appDetails: { data } },
    webhook: { webhookDetails },
  } = state;
  const appId = data['id'];
  const webhookId = webhookDetails['id'];

  try {
    yield call(client, {
      url: `webhooks/${appId}/${webhookId}`,
      method: 'delete',
    });
    yield put(webhookActions.deleteWebhookSuccess());
    if (typeof res === 'function') {
      yield call(res);
    }
  } catch (error: any) {
    yield put(webhookActions.deleteWebhookFail(error));
    if (typeof rej === 'function') {
      yield call(rej);
    }
  } finally {
    setLoading(false);
  }
}

export function* pauseResumeWebhook({ payload: { action, res, rej } }: PayloadAction<IPauseResumeWebhookAction>) {
  setLoading(true);
  const state = yield select();
  const {
    apps: { appDetails: { data } },
    webhook: { webhookDetails },
  } = state;
  const appId = data['id'];
  const webhookId = webhookDetails['id'];

  try {
    const response: WebhookState = yield call(client, {
      url: `webhooks/${appId}/${webhookId}/${action}`,
      method: 'PUT',
    });
    yield put(webhookActions.pauseResumeWebhookSuccess(response));
    if (typeof res === 'function') {
      yield call(res);
    }
  } catch (error: any) {
    yield put(webhookActions.pauseResumeWebhookFail(error));
    if (typeof rej === 'function') {
      yield call(rej);
    }
  } finally {
    setLoading(false);
  }
}

export function* updateWebhook({ payload: { data, res, rej } }: PayloadAction<IUpdateWebhookAction>) {
  setLoading(true);
  const state = yield select();
  const {
    apps: { appDetails: { data: { id } } },
    webhook: { webhookDetails },
  } = state;
  const webhookId = webhookDetails['id'];

  try {
    const response: WebhookState = yield call(client, {
      url: `webhooks/${id}/${webhookId}`,
      data,
      method: 'PATCH',
    });
    yield put(webhookActions.updateWebhookSuccess(response));
    if (typeof res === 'function') {
      yield call(res, response);
    }
  } catch (error: any) {
    yield put(webhookActions.updateWebhookFail(error));
    if (typeof rej === 'function') {
      yield call(rej, error);
    }
  } finally {
    setLoading(false);
  }
}

export function* uploadWebhookCertificate({ payload: { attachments, res, rej } }: PayloadAction<IUploadCertificate>) {
  setLoading(true);
  const state = yield select();
  const {
    apps: { appDetails: { data } },
    webhook: { webhookDetails },
  } = state;
  const appId = data['id'];
  const webhookId = webhookDetails['id'];

  try {
    const response: ICertificates = yield call(client, {
      url: `webhooks/${appId}/${webhookId}/certificates`,
      attachments,
      method: 'POST',
    });
    yield put(webhookActions.uploadWebhookCertificateSuccess(response));
    if (typeof res === 'function') {
      yield call(res, response);
    }
  } catch (error: any) {
    yield put(webhookActions.uploadWebhookCertificateFail(error));
    if (typeof rej === 'function') {
      yield call(rej, error);
    }
  } finally {
    setLoading(false);
  }
}

export function* deleteWebhookCertificate({ payload: { id, res, rej } }: PayloadAction<IDeleteWebhookCertificate>) {
  setLoading(true);
  const state = yield select();
  const {
    apps: { appDetails: { data } },
    webhook: { webhookDetails },
  } = state;
  const appId = data['id'];
  const webhookId = webhookDetails['id'];

  try {
    const response = yield call(client, {
      url: `webhooks/${appId}/${webhookId}/certificates/${id}`,
      method: 'DELETE',
    });
    yield put(webhookActions.deleteWebhookCertificateSuccess({ id }));
    if (typeof res === 'function') {
      yield call(res, response);
    }
  } catch (error) {
    yield put(webhookActions.deleteWebhookCertificateFail(error));
    if (typeof rej === 'function') {
      yield call(rej, error);
    }
  } finally {
    setLoading(false);
  }
}

export default function* webhooksSaga() {
  yield takeLatest(webhookActions.loadWebhookDetails.type, loadWebhookDetails);
  yield takeLatest(webhookActions.generateNewSecret.type, generateNewSecret);
  yield takeLatest(webhookActions.deleteWebhook.type, deleteWebhook);
  yield takeLatest(webhookActions.pauseResumeWebhook.type, pauseResumeWebhook);
  yield takeLatest(webhookActions.updateWebhook.type, updateWebhook);
  yield takeLatest(webhookActions.uploadWebhookCertificate.type, uploadWebhookCertificate);
  yield takeLatest(webhookActions.deleteWebhookCertificate.type, deleteWebhookCertificate);
}
